import { useCallback, useEffect, useRef } from 'react'
import type { ApiFetchInit } from '../api/apiFetch'
import type { TangerLayerSpec } from '../map/tileUrl'
import { useHeatmapLayer, type HeatmapPhase, type HeatmapResponse } from './useHeatmapLayer'
import type { HeatmapContext } from './useContexts'

/** One context's poll state, as reported to whoever owns the screen. */
export interface HeatmapContextState {
  contextId: string
  /** Display name, carried along so the chrome never has to look it up. */
  name: string
  phase: HeatmapPhase
  layer: TangerLayerSpec | null
  coverage: number | null
  /** Restarts this context's poll — and only this one's. */
  retry: () => void
}

type Call = <T>(path: string, init?: Omit<ApiFetchInit, 'token'>) => Promise<T>

/**
 * Owns one surveillance area's heatmap poll and reports its state upward,
 * rendering nothing at all.
 *
 * This shape — a component that renders null — exists for one reason: the
 * poll machine is `useHeatmapLayer`, and the rules of hooks forbid calling a
 * hook once per item of a dynamic list. One component instance per context is
 * how a per-item hook is legally instantiated, and it keeps that hook (and
 * its whole tested clock: 5s → 15s → give up, minus hidden-tab time)
 * unchanged and genuinely per area, which is what it always assumed it was.
 *
 * Each area's poll is deliberately independent: Tanger builds each context's
 * layer separately, so one area can sit PENDING for minutes while another is
 * already UP-TO-DATE, and neither should hold the other back or reset its
 * give-up clock.
 */
export function HeatmapContextPoller({
  context,
  call,
  onState,
}: {
  context: HeatmapContext
  call: Call
  onState: (state: HeatmapContextState) => void
}) {
  const fetchLayer = useCallback(
    () =>
      call<HeatmapResponse>(`/heatmap/${encodeURIComponent(context.id)}`, { method: 'POST' }),
    [call, context.id],
  )

  const { phase, layer, coverage, retry } = useHeatmapLayer(fetchLayer)

  // Read through a ref, and deliberately NOT an effect dependency: the parent
  // stores what it is handed, which re-renders this component and (for an
  // inline lambda) hands over a new `onState` identity every time. Depending
  // on it would report again on every render — a loop whose only brake is
  // the parent's own change detection.
  const onStateRef = useRef(onState)
  onStateRef.current = onState

  useEffect(() => {
    onStateRef.current({
      contextId: context.id,
      name: context.name,
      phase,
      layer,
      coverage,
      retry,
    })
  }, [context.id, context.name, phase, layer, coverage, retry])

  return null
}
